import { Button, Form, Input, Select, message } from "antd";
import { useEffect, useState, useCallback } from "react";
import { SiMicrosoftexcel } from "react-icons/si";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Loading from "../../components/loading";
import API from "../../config/api";
import { DELETE, GET } from "../../utils/apiCalls";
import PageHeader from "../routes/pageHeader";
import RoomDataTable from "./components/dataTable";
import RoomForm from "./components/form";

const MeetingRooms = () => {
  const { search } = useLocation();
  const Events = useSelector((state: any) => state.Events);
  const user = useSelector((state: any) => state.User.user);
  const query = new URLSearchParams(search);
  const { defult } = useSelector((state: any) => state.Events) || {};
  const eventId = defult?.id;
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [meta, setMeta] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState({
    list: true,
    excel: false,
  });
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openForm, setOpenForm] = useState(false);
  const [data, setData] = useState([]);
  const [data1, setData1] = useState(1);

  useEffect(() => {
    fetchData();
  }, [data1]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading((prev) => ({ ...prev, list: true }));
      const params = {
        ...(query.get("query") && { query: query.get("query") }),
        ...(query.get("page") && { page: query.get("page") }),
        ...(query.get("limit") && { take: query.get("limit") }),
        ...(query.get("available") && {
          has_allocated: query.get("available"),
        }),
        ...(query.get("sortField") && { sortField: query.get("sortField") }),
        ...(query.get("sortOrder") && { sortOrder: query.get("sortOrder") }),
        eventId,
      };
      const res: any = await GET(API.ROOM_LIST_SEARCH, params);
      if (res.status) {
        setData(res.data);
        setMeta(res.meta);
      } else {
        message.error("Failed to fetch meetings data.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("An error occurred while fetching meetings.");
    } finally {
      setIsLoading((prev) => ({ ...prev, list: false }));
      setLoading(false);
    }
  }, [query, eventId]);

  const generateExcel = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, excel: true }));
      
      // Use the same API with current filters and sorting, but without pagination
      const params = {
        ...(query.get("query") && { query: query.get("query") }),
        ...(query.get("available") && { has_allocated: query.get("available") }),
        ...(query.get("sortField") && { sortField: query.get("sortField") }),
        ...(query.get("sortOrder") && { sortOrder: query.get("sortOrder") }),
        eventId,
        // Don't include page and take for Excel export to get all data
      };
      
      const response: any = await GET(API.ROOM_LIST_SEARCH, params);
      
      if (response?.status) {
        // Format the data with only required fields
        const formattedData = response.data.map((room: any, index: number) => ({
          No: index + 1,
          Name: room.name || "",
          Status: room.has_allocated ? "Occupied" : "Available",
          "Created At": room.createdAt ? new Date(room.createdAt).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }) : "",
          "Updated At": room.updatedAt ? new Date(room.updatedAt).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }) : "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        
        // Set column widths for better readability
        const colWidths = [
          { wch: 5 },  // No
          { wch: 20 }, // Name
          { wch: 12 }, // Status
          { wch: 20 }, // CreatedAt
          { wch: 20 }  // UpdatedAt
        ];
        worksheet["!cols"] = colWidths;
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "MeetingRooms");
        
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        
        const dataBlob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });
        
        const link = document.createElement("a");
        link.href = URL.createObjectURL(dataBlob);
        link.download = `MeetingRooms-Export-${new Date().toLocaleString()}.xlsx`;
        link.click();
        
        message.success("Excel export completed successfully!");
      } else {
        message.error("Couldn't generate reports");
      }
    } catch (err) {
      console.error(err);
      message.error("Couldn't generate reports");
    } finally {
      setIsLoading((prev) => ({ ...prev, excel: false }));
    }
  };

  const handleEditItem = (value: any) => {
    setSelectedItem(value);
    setOpenForm(true);
  };

  const handleDeleteItem = async (id: any) => {
    try {
      const response: any = await DELETE(API.ROOMS_DELETE + id);
      if (response?.status) {
        fetchData();
        message.success("Deleted successfully.");
      } else {
        message.error("Failed to delete.");
      }
    } catch (err) {
      message.error("An error occurred while deleting.");
    }
  };

  const changeParams = (data: any) => {
    // Preserve sorting parameters if not explicitly provided, similar to Investors module
    const sortField = data?.sortField !== undefined ? data.sortField : query.get("sortField");
    const sortOrder = data?.sortOrder !== undefined ? data.sortOrder : query.get("sortOrder");
    
    const queryParams = [
      { key: "query", value: data?.query !== undefined ? data.query : (data?.val && query.get("query")) },
      { key: "available", value: data?.available !== undefined ? data.available : (data?.val && query.get("available")) },
      { key: "sortField", value: sortField },
      { key: "sortOrder", value: sortOrder },
    ]
      .filter((param: any) => param?.value !== null && param?.value !== undefined && param?.value !== "" && param?.value !== "null")
      .map((param: any) => `&${param.key}=${param?.value}`)
      .join("");
      
    const baseUrl = `/company/rooms?page=${
      data?.page || query.get("page") || 1
    }&limit=${data?.pageSize || query.get("limit") || 10}`;
    
    navigate(`${baseUrl}${queryParams}`);
    setData1((prev) => prev + 1);
  };

  // Updated onSortChange to preserve filters, similar to Investors module
  const onSortChange = (sortField: string | undefined, sortOrder: string | undefined) => {
    changeParams({
      sortField,
      sortOrder,
      page: 1, // Reset to first page when sorting
    });
  };

  // Updated form change handler to preserve sorting, similar to Investors module
  const onFormChange = (_:any, allData: any) => {
    changeParams({
      ...allData,
      page: 1,
      // Preserve existing sorting by explicitly passing current URL params
      sortField: query.get("sortField"),
      sortOrder: query.get("sortOrder"),
    });
  };

  return (
    <div>
      <PageHeader title="Meeting Rooms">
        <Form
          form={form}
          onValuesChange={onFormChange}
          initialValues={{
            query: query.get("query"),
            available: query.get("available") || undefined,
          }}
        >
          <div className="AdminHeader-extra">
            <Form.Item name="available" noStyle>
              <Select
                placeholder="Availability"
                allowClear
                style={{ minWidth: 140, marginRight: 8 }}
                options={[
                  { label: "Available", value: "false" },
                  { label: "Occupied", value: "true" },
                ]}
              />
            </Form.Item>
            <Form.Item name="query" noStyle>
              <Input style={{ width: 200 }} placeholder="Search" allowClear />
            </Form.Item>
            {Array.isArray(user.access[Events?.defult?.id]) &&
            user.access[Events?.defult?.id].includes(8) ? null : 
            <>
              <Button type="primary" onClick={() => handleEditItem(null)}>
                ADD ROOMS +
              </Button>
              <Button
                loading={isLoading.excel}
                onClick={generateExcel}
                style={{ padding: 5, background: "#fff", cursor: "pointer" }}
              >
                EXPORT <SiMicrosoftexcel size={20} color="green" />
              </Button>
            </>}
          </div>
        </Form>
      </PageHeader>
      {loading ? (
        <Loading />
      ) : (
        <RoomDataTable
        data={data}
        meta={meta}
        loading={isLoading.list}
        onPageChange={(page: number, pageSize: number) =>
          changeParams({ page, pageSize, val: true })
        }
        onSortChange={onSortChange}
        onChange={handleEditItem}
        delete={handleDeleteItem}
      />
      )}
      {openForm && (
        <RoomForm
          open={openForm}
          data={selectedItem}
          onClose={() => setOpenForm(false)}
          reload={fetchData}
        />
      )}
      <br />
    </div>
  );
};

export default MeetingRooms;